import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Square, Trash2 } from 'lucide-react';
import { reconApi } from '@/shared/api';
import { format } from 'date-fns';

export function ReconJobListPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['recon-jobs'],
    queryFn: () => reconApi.list(),
    refetchInterval: 15_000,
  });

  // Auto-backfill DP IDs for jobs that ran before dp_client_id extraction
  // existed. For each completed job missing a dp_client_id, call the backfill
  // endpoint once (it re-reads the DP file header). Guarded by a ref so each
  // job is attempted at most once per mount.
  const dpBackfillTried = useRef(new Set<string>());
  useEffect(() => {
    const rows = data?.rows ?? [];
    const todo = rows.filter(j =>
      (j.status === 'awaiting_review' || j.status === 'completed') &&
      j.summary != null &&
      j.summary.dp_client_id == null &&
      !dpBackfillTried.current.has(j.id)
    );
    if (todo.length === 0) return;
    let anyFilled = false;
    void Promise.allSettled(
      todo.map(async j => {
        dpBackfillTried.current.add(j.id);
        try {
          const res = await reconApi.backfillDpClientId(j.id);
          if (res.dp_client_id) anyFilled = true;
        } catch { /* ignore individual failures */ }
      }),
    ).then(() => {
      if (anyFilled) void queryClient.invalidateQueries({ queryKey: ['recon-jobs'] });
    });
  }, [data, queryClient]);

  // Per-row cancel. We use a single mutation and pass the job_id at call
  // time; this means only one cancel can be in flight at a time, which is
  // fine for a UI affordance (the user clicks one button, waits for it).
  const cancelMutation = useMutation({
    mutationFn: (job_id: string) => reconApi.cancel(job_id, 'Cancelled from jobs list'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recon-jobs'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (job_id: string) => reconApi.delete(job_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recon-jobs'] });
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-axis-ink">Portfolio – DP Reconciliation</h1>
          <p className="text-sm text-axis-ash mt-1">
            Cross-check broker portfolio holdings against DP demat balances. Mismatches are classified by root cause.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link to="/recon/new" className="btn-primary">
            <Plus className="w-4 h-4" />
            New reconciliation
          </Link>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Job ID</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">PF ID</th>
              <th className="text-left px-4 py-3">DP ID</th>
              <th className="text-right px-4 py-3">Match rate</th>
              <th className="text-right px-4 py-3">Mismatches</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-axis-ash">Loading…</td></tr>
            ) : !data?.rows?.length ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-axis-ash">
                No reconciliations yet. <Link to="/recon/new" className="text-axis-maroon underline">Start one</Link>.
              </td></tr>
            ) : (() => {
              // A DP ID is "shared" ONLY when it is used by more than one
              // DISTINCT PF ID. The same PF+DP job re-run many times must NOT
              // count — so we track the set of distinct PF IDs per DP ID.
              const pfIdsByDp = new Map<string, Set<string>>();
              for (const j of data.rows) {
                const dp = j.summary?.dp_client_id;
                const pf = j.summary?.pf_client_id;
                if (!dp || !pf) continue;
                if (!pfIdsByDp.has(dp)) pfIdsByDp.set(dp, new Set());
                pfIdsByDp.get(dp)!.add(pf);
              }
              const sharedDpIds = new Set(
                [...pfIdsByDp.entries()].filter(([, pfs]) => pfs.size > 1).map(([dp]) => dp),
              );
              return data.rows.map((job) => {
              // Prefer the LIVE stats saved by the detail page (exactly what the
              // user sees inside the job). Fall back to deriving from the stored
              // summary buckets if a job hasn't been opened since the live save
              // was introduced.
              const liveMis = job.summary?.live_mismatches;
              const liveRate = job.summary?.live_match_rate_pct;
              const derivedMismatches = job.summary
                ? job.summary.qty_mismatch + job.summary.only_in_portfolio + job.summary.only_in_dp
                : 0;
              const derivedTotal = job.summary
                ? job.summary.total_rows - job.summary.isin_unresolvable
                : 0;
              const derivedRate = derivedTotal > 0
                ? ((derivedTotal - derivedMismatches) / derivedTotal) * 100
                : 0;
              const totalMismatches = (liveMis != null) ? liveMis : derivedMismatches;
              const matchRatePct = (liveRate != null) ? liveRate : derivedRate;
              return (
                <tr
                  key={job.id}
                  onClick={() => navigate(`/recon/${job.id}`)}
                  className="hover:bg-axis-cloud-50 cursor-pointer"
                >
                  <td className="px-4 py-3 font-mono text-xs">{job.id.slice(0, 12)}…</td>
                  <td className="px-4 py-3">
                    <span
                      // For failed jobs, surface the error message as a
                      // native tooltip so an operator can read the cause
                      // without drilling in.
                      title={job.status === 'failed' && job.error_message ? job.error_message : undefined}
                      className={
                        job.status === 'completed'
                          ? 'pill bg-emerald-100 text-emerald-800 border border-emerald-200'
                          : job.status === 'awaiting_review'
                            ? 'pill bg-amber-100 text-amber-800 border border-amber-200'
                            : job.status === 'failed'
                              ? 'pill bg-rose-100 text-rose-800 border border-rose-200 cursor-help'
                              : 'pill bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200'
                    }>{job.status}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {job.summary?.pf_client_id ?? <span className="text-axis-ash">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {job.summary?.dp_client_id ? (
                      <span className="inline-flex items-center gap-1">
                        {job.summary.dp_client_id}
                        {sharedDpIds.has(job.summary.dp_client_id) && (
                          <span
                            style={{ color: '#8b5cf6' }}
                            title="Shared DP file — this DP ID is used by more than one PF file"
                          >★</span>
                        )}
                      </span>
                    ) : <span className="text-axis-ash">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {job.summary ? `${matchRatePct.toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {job.summary ? totalMismatches : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-axis-ash">
                    {format(new Date(job.created_at), 'd MMM yyyy, HH:mm:ss.SSS')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      {(job.status === 'queued' || job.status === 'processing') && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('Cancel this reconciliation? In-flight Claude calls will be aborted.')) {
                              cancelMutation.mutate(job.id);
                            }
                          }}
                          disabled={cancelMutation.isPending}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition disabled:opacity-50"
                          style={{
                            background: 'color-mix(in srgb, var(--status-err) 10%, transparent)',
                            color: 'var(--status-err)',
                            border: '1px solid color-mix(in srgb, var(--status-err) 35%, transparent)',
                          }}
                          title="Cancel this job"
                        >
                          <Square className="w-3 h-3" fill="currentColor" />
                          Cancel
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Delete job ${job.id}?\n\nThis permanently removes the job, all results, and the uploaded files from storage. This cannot be undone.`)) {
                            deleteMutation.mutate(job.id);
                          }
                        }}
                        disabled={deleteMutation.isPending && deleteMutation.variables === job.id}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition disabled:opacity-50 opacity-40 hover:opacity-100"
                        style={{
                          background: 'color-mix(in srgb, var(--status-err) 8%, transparent)',
                          color: 'var(--status-err)',
                          border: '1px solid color-mix(in srgb, var(--status-err) 25%, transparent)',
                        }}
                        title="Permanently delete this job and all its data"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
